import React from "react"
import styled from "styled-components"
import Img from "gatsby-image"
import { graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiTrendingUp,
  FiMessageSquare,
  FiGitMerge,
  FiEye,
  FiFileText,
  FiActivity,
  FiPlus,
} from "react-icons/fi"
import Link from "../components/Link"
import PageMetadata from "../components/PageMetadata"
import { translateMessageId } from "../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(1.9rem, 3vw, 2.75rem);
    font-weight: 500;
    line-height: 1.15;
    letter-spacing: -0.035em;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin: 0 0 0.9rem;
  }
  a:focus-visible,
  summary:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  color: #fff;
  background: #0b1b2a;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      rgba(4, 14, 24, 0.35) 0%,
      rgba(4, 14, 24, 0.2) 43%,
      rgba(4, 14, 24, 0.05) 80%
    );
  }
  a:focus-visible,
  summary:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  width: 100%;
  height: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: clamp(250px, 48vw, 340px);
  }
`
const HeroContent = styled(Container)`
  min-height: 610px;
  padding-top: 4.5rem;
  padding-bottom: 4.5rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  h1 {
    max-width: 680px;
    color: #fff;
    font-size: clamp(2.75rem, 5.1vw, 4.5rem);
    font-weight: 500;
    line-height: 1.07;
    letter-spacing: -0.045em;
    margin: 0 0 1.75rem;
    text-wrap: balance;
  }
  > p {
    max-width: 500px;
    color: #e0e6ec;
    font-size: 1.1rem;
    line-height: 1.75;
    margin: 0 0 2rem;
  }
  @media (max-width: 750px) {
    order: 1;
    min-height: auto;
    padding-top: 3rem;
    padding-bottom: 3rem;
    h1 {
      font-size: clamp(2.5rem, 8.8vw, 3.75rem);
    }
    > p {
      font-size: 1rem;
    }
  }
`
const Kicker = styled.p`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.primary};
`
const HeroKicker = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  margin-bottom: 1.75rem;
  &::before {
    content: "";
    flex-shrink: 0;
    width: 2rem;
    height: 2px;
    background: #6d9dff;
  }
`
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
`
const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 1.2rem;
  padding: 1rem 1.25rem;
  border: 1px solid #fff;
  background: #fff;
  color: #102030 !important;
  font-size: 0.95rem;
  font-weight: 600;
  text-decoration: none;
  &:hover {
    background: #dce7ff;
    border-color: #dce7ff;
  }
  svg {
    flex-shrink: 0;
  }
`
const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  color: #fff !important;
  font-size: 0.95rem;
  text-decoration: none;
  padding: 0.75rem 0;
  border-bottom: 1px solid #8896a5;
  &:hover {
    border-color: #fff;
  }
`
const SectionNav = styled.nav`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.95rem;
    padding: 1.4rem 1.5rem;
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    text-decoration: none;
  }
  a:last-child {
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.background};
  }
  svg {
    flex-shrink: 0;
  }
  @media (max-width: 600px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    a {
      padding: 1rem 0;
      border-left: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:last-child {
      border-right: 0;
      border-bottom: 0;
    }
  }
`
const Section = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const SectionIntro = styled.div`
  max-width: 770px;
  margin-bottom: 2.75rem;
  > p:last-child {
    max-width: 680px;
    font-size: 1.1rem;
    line-height: 1.75;
    color: ${({ theme }) => theme.colors.text200};
    margin: 0;
  }
`
const Capabilities = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.25rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`
const Capability = styled.article`
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  padding: 2rem;
  display: grid;
  grid-template-columns: 2rem 1fr;
  gap: 1.5rem;
  > svg {
    color: ${({ theme }) => theme.colors.primary};
    margin-top: 0.1rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
    margin: 0;
  }
  @media (max-width: 400px) {
    padding: 1.5rem;
    gap: 1rem;
    grid-template-columns: 1.5rem 1fr;
  }
`
const Method = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const Steps = styled.ol`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 2rem;
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    margin: 0;
    padding: 1.5rem 0 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  span {
    display: block;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.8rem;
    letter-spacing: 0.08em;
    margin-bottom: 1.75rem;
  }
  h3 {
    font-size: 1.15rem;
  }
  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
  }
  @media (max-width: 950px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 550px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    span {
      margin-bottom: 0.75rem;
    }
  }
`
const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  gap: 4.5rem;
  ${SectionIntro} {
    margin-bottom: 0;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`
const TechGroup = styled.div`
  padding: 1.5rem 0;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  h3 {
    font-size: 0.85rem;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text200};
    margin-bottom: 1rem;
  }
  div {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
  }
  span {
    padding: 0.55rem 0.8rem;
    background: ${({ theme }) => theme.colors.ednBackground};
    font-size: 0.95rem;
  }
`
const Related = styled.aside`
  margin-top: 4rem;
  padding-top: 2.5rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h2 {
    font-size: 1.1rem;
    letter-spacing: 0;
  }
  > div {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem 2.5rem;
  }
  a {
    display: inline-flex;
    align-items: center;
    gap: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    text-decoration: none;
    font-size: 0.95rem;
    padding: 0.4rem 0;
  }
  a:hover {
    text-decoration: underline;
  }
`
const Contact = styled(Section)`
  color: #fff;
  background: #0b1b2a;
  ${Container} {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 3rem;
  }
  ${Kicker} {
    color: #b4cae7;
  }
  h2 {
    color: #fff;
    max-width: 640px;
  }
  p:last-child {
    color: #d9e2eb;
    max-width: 670px;
    line-height: 1.75;
    font-size: 1.05rem;
    margin: 0;
  }
  a:focus-visible,
  summary:focus-visible {
    outline-color: #fff;
  }
  @media (max-width: 950px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    a {
      justify-self: start;
    }
  }
`

const Applications = styled(Section)`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
`
const UseCases = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2.5rem;
  article {
    border-top: 2px solid ${({ theme }) => theme.colors.primary};
    padding-top: 1.5rem;
  }
  h3 {
    font-size: 1.45rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
  }
  p:last-child {
    margin-bottom: 0;
  }
  strong {
    display: block;
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.8rem;
    font-weight: 600;
    margin-bottom: 0.4rem;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`
const Approach = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
`
const FAQSection = styled(Section)`
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: 0.8fr 1.2fr;
    gap: 4rem;
  }
  ${SectionIntro} {
    margin: 0;
  }
  @media (max-width: 850px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`
const Question = styled.details`
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    padding: 1.4rem 0;
    cursor: pointer;
    list-style: none;
    font-size: 1.05rem;
    font-weight: 500;
    line-height: 1.5;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  summary svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  &[open] summary svg {
    transform: rotate(45deg);
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    line-height: 1.75;
    margin: 0 0 1.5rem;
  }
`

const capabilities = [
  { id: "predictive", icon: FiTrendingUp },
  { id: "generative", icon: FiMessageSquare },
  { id: "agents", icon: FiGitMerge },
  { id: "vision", icon: FiEye },
  { id: "documents", icon: FiFileText },
  { id: "mlops", icon: FiActivity },
]
const useCases = ["demand", "knowledge", "workflows"]
const steps = ["discover", "prepare", "validate", "operate"]
const technologies = [
  {
    id: "models",
    tools: ["Python", "scikit-learn", "TensorFlow", "Keras", "YOLO"],
  },
  { id: "integration", tools: ["APIs", "Node.js", "Python", "Microservices"] },
  {
    id: "cloud",
    tools: ["AWS", "Microsoft Azure", "Google Cloud", "On-premise"],
  },
]
const questions = ["start", "data", "training", "privacy"]
const related = [
  { id: "development", to: "/desarrollo/" },
  { id: "architecture", to: "/arquitectura/detalle/" },
  { id: "security", to: "/devsecops/" },
]

const AIServicePage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`ai-service-${id}`, intl)
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.hero.childImageSharp.fixed.src}
      />
      <Hero>
        <HeroImage
          fluid={data.hero.childImageSharp.fluid}
          alt=""
          loading="eager"
          fadeIn={false}
          imgStyle={{ objectPosition: "80% center" }}
        />
        <HeroContent>
          <HeroKicker>{t("eyebrow")}</HeroKicker>
          <h1>{t("title")}</h1>
          <p>{t("intro")}</p>
          <Actions>
            <PrimaryLink to="/#contacto">
              {t("contact")} <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <SecondaryLink to="#soluciones-ia">
              {t("explore")} <FiArrowDown aria-hidden="true" />
            </SecondaryLink>
          </Actions>
        </HeroContent>
      </Hero>
      <SectionNav aria-label={t("navigation")}>
        <Container>
          <Link to="#soluciones-ia">
            {t("nav-solutions")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#aplicaciones">
            {t("nav-applications")} <FiArrowDown aria-hidden="true" />
          </Link>
          <Link to="#metodo">
            {t("nav-method")} <FiArrowDown aria-hidden="true" />
          </Link>
        </Container>
      </SectionNav>
      <Section id="soluciones-ia" aria-labelledby="ai-solutions-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("solutions-eyebrow")}</Kicker>
            <h2 id="ai-solutions-title">{t("solutions-title")}</h2>
            <p>{t("solutions-intro")}</p>
          </SectionIntro>
          <Capabilities>
            {capabilities.map(({ id, icon: Icon }) => (
              <Capability key={id}>
                <Icon size={26} aria-hidden="true" />
                <div>
                  <h3>{t(`${id}-title`)}</h3>
                  <p>{t(`${id}-description`)}</p>
                </div>
              </Capability>
            ))}
          </Capabilities>
        </Container>
      </Section>
      <Applications id="aplicaciones" aria-labelledby="ai-applications-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("applications-eyebrow")}</Kicker>
            <h2 id="ai-applications-title">{t("applications-title")}</h2>
            <p>{t("applications-intro")}</p>
          </SectionIntro>
          <UseCases>
            {useCases.map((id) => (
              <article key={id}>
                <Approach>{t(`${id}-approach`)}</Approach>
                <h3>{t(`${id}-title`)}</h3>
                <p>{t(`${id}-description`)}</p>
                <p>
                  <strong>{t("evaluation-label")}</strong>
                  {t(`${id}-evaluation`)}
                </p>
              </article>
            ))}
          </UseCases>
        </Container>
      </Applications>
      <Method id="metodo" aria-labelledby="ai-method-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("method-eyebrow")}</Kicker>
            <h2 id="ai-method-title">{t("method-title")}</h2>
            <p>{t("method-intro")}</p>
          </SectionIntro>
          <Steps>
            {steps.map((step, index) => (
              <li key={step}>
                <span aria-hidden="true">0{index + 1}</span>
                <h3>{t(`${step}-title`)}</h3>
                <p>{t(`${step}-description`)}</p>
              </li>
            ))}
          </Steps>
        </Container>
      </Method>
      <Section aria-labelledby="ai-technology-title">
        <Container>
          <TechnologyGrid>
            <SectionIntro>
              <Kicker>{t("technology-eyebrow")}</Kicker>
              <h2 id="ai-technology-title">{t("technology-title")}</h2>
              <p>{t("technology-intro")}</p>
            </SectionIntro>
            <div>
              {technologies.map(({ id, tools }) => (
                <TechGroup key={id}>
                  <h3>{t(`tech-${id}`)}</h3>
                  <div>
                    {tools.map((tool) => (
                      <span key={tool}>
                        {tool === "Microservices" ? t("microservices") : tool}
                      </span>
                    ))}
                  </div>
                </TechGroup>
              ))}
            </div>
          </TechnologyGrid>
          <Related aria-labelledby="ai-related-title">
            <h2 id="ai-related-title">{t("related-title")}</h2>
            <div>
              {related.map(({ id, to }) => (
                <Link key={id} to={to}>
                  {t(`related-${id}`)} <FiArrowRight aria-hidden="true" />
                </Link>
              ))}
            </div>
          </Related>
        </Container>
      </Section>
      <FAQSection aria-labelledby="ai-faq-title">
        <Container>
          <SectionIntro>
            <Kicker>{t("faq-eyebrow")}</Kicker>
            <h2 id="ai-faq-title">{t("faq-title")}</h2>
            <p>{t("faq-intro")}</p>
          </SectionIntro>
          <div>
            {questions.map((id) => (
              <Question key={id}>
                <summary>
                  {t(`faq-${id}-question`)}
                  <FiPlus size={20} aria-hidden="true" />
                </summary>
                <p>{t(`faq-${id}-answer`)}</p>
              </Question>
            ))}
          </div>
        </Container>
      </FAQSection>
      <Contact aria-labelledby="ai-contact-title">
        <Container>
          <div>
            <Kicker>{t("cta-eyebrow")}</Kicker>
            <h2 id="ai-contact-title">{t("cta-title")}</h2>
            <p>{t("cta-description")}</p>
          </div>
          <PrimaryLink to="/#contacto">
            {t("contact")} <FiArrowRight aria-hidden="true" />
          </PrimaryLink>
        </Container>
      </Contact>
    </Page>
  )
}

export default AIServicePage

export const query = graphql`
  query AIServicePageQuery {
    hero: file(relativePath: { eq: "ai/enterprise-ai-v1.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
  }
`
